import { SPECS } from '../../../../constants';
import { CheckoutService } from './CheckoutService';
import { CartModel } from '../models/Cart.model';
import { SummaryModel } from '../models/Summary.model';
import { getPaymentAmount } from '../utils/getPaymentAmount';
import { getDiscountsToAlignWith } from '../utils/discounts.utils';
import { getOrderItemsFromCheckout } from '../utils/getOrderItemsFromCheckout';
import { getPaymentBreakdown } from '../utils/getPaymentBreakdown';
import {
  shouldNavigateToFastFlowCheckout,
  shouldNavigateToFastFlowCheckoutAfterPlaceOrderError,
} from '../utils/shouldNavigateToFastFlow';
import { NavigationService } from './NavigationService';
import { ExpressCheckoutSettingsService } from './ExpressCheckoutSettingsService';
import type { EcomPlatformViewerScriptContext } from '../../../../viewer-script/types';
import type {
  ExpressPaymentData,
  ExpressAuthorizeResult,
  ExpressContact,
  ExpressPaymentAuthorized,
  ExpressShippingMethod,
} from '../types';
import { ExpressAuthorizeStatus, ExpressShippingError } from '../types';
import { ExpressCheckoutBIService } from './ExpressCheckoutBIService';
import { trackInitiateCheckout } from '../utils/analytics.utils';
import { hasShippableItems } from '../utils/hasShippableItems';

export class ExpressCheckoutService {
  private readonly checkoutService: CheckoutService;
  private readonly settingsService: ExpressCheckoutSettingsService;
  private readonly context: EcomPlatformViewerScriptContext;
  private readonly expressCheckoutBIService: ExpressCheckoutBIService;
  static getInstance(
    context: EcomPlatformViewerScriptContext,
  ): ExpressCheckoutService {
    if (!context.expressCheckoutService) {
      context.expressCheckoutService = new ExpressCheckoutService(context);
    }
    return context.expressCheckoutService;
  }

  private constructor(context: EcomPlatformViewerScriptContext) {
    this.checkoutService = new CheckoutService({
      httpClient: context.platformServices!.essentials.httpClient,
      errorHandler: context.platformServices!.essentials.errorHandler,
      context,
      cartService: context.cartService,
      locale: context.wixApi!.window.locale,
    });
    this.settingsService = ExpressCheckoutSettingsService.fromContext(context);
    this.context = context;
    this.expressCheckoutBIService = new ExpressCheckoutBIService(context);
  }

  public readonly getInitialPaymentData = async (
    checkoutId: string,
  ): Promise<{
    paymentData: ExpressPaymentData;
    extraData: {
      priceVerificationToken?: string;
      deliveryMethodCodeFromSummary?: string;
    };
  }> => {
    const { cart, summary } =
      await this.checkoutService.getCheckoutByCheckoutId(checkoutId);
    this.context.wixApi!.window.trackEvent(...trackInitiateCheckout(cart));

    return {
      paymentData: {
        ...this.calculatePaymentData(cart, summary),
      } as ExpressPaymentData,
      extraData: {
        priceVerificationToken: summary.priceVerificationToken,
        deliveryMethodCodeFromSummary: summary.deliveryMethodCodeFromSummary,
      },
    };
  };

  public readonly onShippingContactSelected = async (
    checkoutId: string,
    contact: ExpressContact,
  ): Promise<{
    paymentData: Partial<ExpressPaymentData> & {
      shippingMethods?: ExpressShippingMethod[];
      error?: ExpressShippingError;
    };
    extraData?: {
      priceVerificationToken?: string;
      deliveryMethodCodeFromSummary?: string;
      deliveryOptionsErrors?: {
        hasShippingZipCodeError?: boolean;
        hasShippingSubdivisionError?: boolean;
      };
    };
  }> => {
    try {
      const { cart, summary, shippingMethods, deliveryOptionsErrors } =
        await this.checkoutService.updateDeliveryAddress(checkoutId, contact);

      if (shippingMethods && shippingMethods.length === 0) {
        return {
          paymentData: { error: ExpressShippingError.AddressUnserviceable },
        };
      }

      return {
        paymentData: {
          ...this.calculatePaymentData(cart, summary),
          shippingMethods,
        },
        extraData: {
          priceVerificationToken: summary.priceVerificationToken,
          deliveryMethodCodeFromSummary: summary.deliveryMethodCodeFromSummary,
          deliveryOptionsErrors,
        },
      };
    } catch {
      return {
        paymentData: { error: ExpressShippingError.AddressUnserviceable },
      };
    }
  };

  public readonly onShippingMethodSelected = async (
    checkoutId: string,
    methodId: string,
  ): Promise<{
    paymentData: ExpressPaymentData;
    extraData?: {
      priceVerificationToken?: string;
      deliveryMethodCodeFromSummary?: string;
    };
  }> => {
    const { cart, summary } = await this.checkoutService.setDeliveryMethod(
      checkoutId,
      methodId,
    );

    return {
      paymentData: {
        ...this.calculatePaymentData(cart, summary),
      } as ExpressPaymentData,
      extraData: {
        priceVerificationToken: summary.priceVerificationToken,
        deliveryMethodCodeFromSummary: summary.deliveryMethodCodeFromSummary,
      },
    };
  };

  private readonly calculatePaymentData = (
    cart: CartModel,
    summary: SummaryModel,
  ): Partial<ExpressPaymentData> => {
    const ecomAlignLineItemCouponsInExpressCheckout =
      this.context.platformServices!.essentials.experiments.enabled(
        SPECS.ecomAlignLineItemCouponsInExpressCheckout,
      );
    const discountsToAlignWith = getDiscountsToAlignWith(
      cart,
      summary,
      ecomAlignLineItemCouponsInExpressCheckout,
    );
    return {
      orderItems: getOrderItemsFromCheckout(
        cart,
        summary,
        discountsToAlignWith,
      ),
      paymentBreakdown: getPaymentBreakdown(
        cart,
        summary,
        discountsToAlignWith,
      ),
      paymentAmount: getPaymentAmount(summary),
    };
  };

  public readonly onPaymentAuthorized = async (
    checkoutId: string,
    payment: ExpressPaymentAuthorized,
    {
      shouldUpdateShippingAddress,
      priceVerificationToken,
      deliveryOptionsErrors,
      numberOfShippingMethods,
      deliveryMethodCodeFromSummary,
    }: {
      shouldUpdateShippingAddress: boolean;
      priceVerificationToken?: string;
      deliveryOptionsErrors?: {
        hasShippingZipCodeError?: boolean;
        hasShippingSubdivisionError?: boolean;
      };
      numberOfShippingMethods: number;
      deliveryMethodCodeFromSummary?: string;
    },
    {
      contentAppId,
      contentComponentId,
      successUrl,
      continueShoppingUrl,
    }: {
      contentAppId?: string;
      contentComponentId?: string;
      successUrl?: string;
      continueShoppingUrl?: string;
    } = {},
  ): Promise<ExpressAuthorizeResult> => {
    const navigationService = new NavigationService(this.context, {
      continueShoppingUrl,
      successUrl,
    });
    if (payment.error) {
      return { status: ExpressAuthorizeStatus.Error };
    }
    this.expressCheckoutBIService.clickContinueEWalletModalSf(checkoutId);

    let cart: CartModel;
    try {
      cart = await this.checkoutService.updateDeliveryAndBillingInfo(
        checkoutId,
        payment.billingContact,
        payment.shippingContact,
        { shouldUpdateShippingAddress },
      );
    } catch {
      return { status: ExpressAuthorizeStatus.Error };
    }

    const shouldApplyDeliveryMethod =
      shouldUpdateShippingAddress &&
      hasShippableItems(cart.lineItems) &&
      numberOfShippingMethods === 1 &&
      deliveryMethodCodeFromSummary &&
      cart.deliveryMethodCode !== deliveryMethodCodeFromSummary;
    if (shouldApplyDeliveryMethod) {
      try {
        const result = await this.checkoutService.setDeliveryMethod(
          checkoutId,
          deliveryMethodCodeFromSummary,
        );
        cart = result.cart;
      } catch {
        return { status: ExpressAuthorizeStatus.Error };
      }
    }

    const paymentToken = payment.detailsId!;

    const expressCheckoutSettings = await this.settingsService.getSettings();
    if (
      await shouldNavigateToFastFlowCheckout({
        lineItems: cart.lineItems,
        checkoutId,
        purchaseFlowId: cart.purchaseFlowId,
        expressCheckoutSettings,
        checkoutService: this.checkoutService,
        biService: this.expressCheckoutBIService,
        isMemberLoggedIn: this.context.wixApi!.user.currentUser.loggedIn,
        deliveryOptionsErrors,
        shouldUpdateShippingAddress,
        ecomFastFlowRedirectForAllPolicies:
          this.context.platformServices!.essentials.experiments.enabled(
            SPECS.ecomFastFlowRedirectForAllPolicies,
          ),
      })
    ) {
      await navigationService.navigateToCheckout(checkoutId, paymentToken);
      return { status: ExpressAuthorizeStatus.Success };
    }

    const redirectUrls = await this.getRedirectUrls(
      navigationService,
      checkoutId,
      paymentToken,
      contentAppId!,
      contentComponentId!,
    );
    let placeOrderResult: {
      orderId?: string;
      paymentResponseToken?: string | null;
    };
    try {
      const placeOrderParams = {
        id: checkoutId,
        paymentToken,
        redirectUrls,
        priceVerificationToken,
      };
      placeOrderResult = await this.checkoutService.placeOrder(
        placeOrderParams,
      );
    } catch (e) {
      if (
        shouldNavigateToFastFlowCheckoutAfterPlaceOrderError({
          error: e,
          checkoutId,
          purchaseFlowId: cart.purchaseFlowId,
          biService: this.expressCheckoutBIService,
          ecomCartValidationsInPurchaseFlow:
            this.context.platformServices!.essentials.experiments.enabled(
              SPECS.ecomCartValidationsInPurchaseFlow,
            ),
        })
      ) {
        await navigationService.navigateToCheckout(checkoutId, paymentToken);
        return { status: ExpressAuthorizeStatus.Success };
      }
      return { status: ExpressAuthorizeStatus.Error };
    }

    const thankYouPageUrl = await navigationService.getThankYouPageUrl(
      placeOrderResult.orderId!,
    );

    return {
      status: ExpressAuthorizeStatus.SuccessPendingConfirm,
      paymentResponseToken: placeOrderResult.paymentResponseToken,
      thankYouPageUrl,
    };
  };

  private readonly getRedirectUrls = async (
    navigationService: NavigationService,
    checkoutId: string,
    detailsId: string,
    contentAppId: string,
    contentComponentId: string,
  ) => {
    const thankYouPageUrl =
      await navigationService.getThankYouPageUrlForPlaceOrder();
    return {
      errorUrl: await navigationService.getCheckoutErrorUrl(
        checkoutId,
        detailsId,
        contentAppId,
        contentComponentId,
      ),
      cancelUrl: await navigationService.getCheckoutCancelUrl(
        checkoutId,
        detailsId,
        contentAppId,
        contentComponentId,
      ),
      successUrl: thankYouPageUrl,
      pendingUrl: thankYouPageUrl,
    };
  };
}
